import React, { useMemo } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Table,
  Stack,
  Avatar,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TablePagination,
  CircularProgress,
  Pagination,
} from "@mui/material";
import SearchNotFound from "../../components/SearchNotFound";
import CustomPopoverSection from "src/components/MenuOption/CustomPopoverSection";
import roundFilterList from "@iconify/icons-ic/round-filter-list";
import {
  UserListHead,
  UserListToolbar,
} from "../../components/_dashboard/user";
import { clientPortalUrl, s3baseUrl, harmonyRingEnabled } from "../../config/config";
import { useSnackbar } from "notistack";
import Label from "src/components/Label";
import { makeStyles } from "@mui/styles";
import {
  DeleteMemberApi,
  DeleteMultiMemberApi,
  memberListing,
  member_login_api,
} from "src/DAL/member/member";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import CustomDrawer from "src/components/DrawerForm/CustomDrawer";
import { dd_date_format } from "src/utils/constant";
import MemberFilter from "./MemberFilterNew";
import FilteredChipNew from "src/components/FilteredChipNew";
import { useContentSetting } from "src/Hooks/ContentSettingState";
import ReviewDateMemberModal from "src/components/GeneralComponents/ReviewDateMemberModal";

const TABLE_HEAD = [
  { id: "action", label: "Action", alignRight: false },
  { id: "number", label: "#", alignRight: false },
  { id: "member", label: "Member", alignRight: false },
  { id: "program", label: "Programme", alignRight: false },
  { id: "createdBy", label: "Created By", alignRight: false },
  { id: "CreatedAt", label: "Created At", alignRight: false },
  { id: "status", label: "Status", alignRight: false },
];

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

export default function MembersList() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [openDelete, setOpenDelete] = useState(false);
  const [openMultiDelete, setOpenMultiDelete] = useState(false);
  const classes = useStyles();
  const [deleteDoc, setDeleteDoc] = useState("");
  const {
    setIsOpenNotePad,
    setSelectedNotepad,
    setIsOpenFormsList,
    setSelectedFormsMember,
  } = useContentSetting();
  const [openReviewDateModal, setOpenReviewDateModal] = useState(false);
  const [selectedMemberForReviewDate, setSelectedMemberForReviewDate] =
    useState(null);
  const [filterDrawerState, setFilterDrawerState] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [csvData, setCsvData] = useState([]);
  const [searchText, setSearchText] = useState("");

  const EMPTY_FILTER = useMemo(
    () => ({
      program: [],
      statuss: "all",
      search_text: "",
      filter: "",
    }),
    [],
  );

  const [filterState, setFilterState] = useState(EMPTY_FILTER);
  const [appliedFilterState, setAppliedFilterState] = useState(EMPTY_FILTER);
  const [filterStateUpdated, setFilterStateUpdated] = useState(EMPTY_FILTER);

  let listing_name = "/member/";

  const fetchMembers = async () => {
    let postData = {
      program: appliedFilterState.program,
      status: appliedFilterState.statuss,
      search_text: appliedFilterState.search_text,
      filter: appliedFilterState.filter,
    };

    if (postData?.program.length > 0) {
      postData.program = postData.program.map((program) => {
        return program._id;
      });
    }

    const result = await memberListing(page, rowsPerPage, postData);

    if (result.code === 200) {
      const members = result?.member?.map((member, index) => {
        return {
          ...member,
          id: member._id,
          fulName: member.first_name + " " + member.last_name,
          first_name: member.first_name,
          last_name: member.last_name,
          name: member.first_name,
          lastName: member.last_name,
          status: member.status,
          email: member.email,
          profile: member.profile_image,
          program: member.program,
          goal: member.goal_statement_status,
          contact_number: member.contact_number,
          street: member.street,
          city: member.city,
          zip_code: member.zip_code,
          state: member.state,
          selectedCountry: member.country,
          createdBy: member.created_by ? member.created_by : "Admin",
          time_zone: member.time_zone,
          member_biography: member.member_biography,
          profile_image: member.profile_image,
          first_payment_date: member.first_payment_date,
          selected_country: member.country,
          communityLevel: member.community_level,
          likeCount: member.like_count,
          gratitudeCount: member.gratitude_count,
          coinsCount: member.coins_count,
          createdAt: member.createdAt,
          gaolStatementCompletedStatus: member.goal_statement_completed_status,
          gaolStatementCompletedDate: member.goal_statement_completed_date,
          save_and_close_status: member.save_and_close_status,
          save_and_close_date: member.save_and_close_date,
          referral_name: member.refferal_name,
          action_info: member.action_info,
          non_compliance_member: harmonyRingEnabled && member?.is_compliant,
          object: member,
          created_at: dd_date_format(member.createdAt),
          count: index + 1 + rowsPerPage * page,
        };
      });

      return {
        members,
        totalPages: result.total_pages,
        totalCount: result.total_member_count,
      };
    } else {
      throw new Error(result.message);
    }
  };

  const {
    data: queryData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      "members",
      page,
      rowsPerPage,
      JSON.stringify(appliedFilterState),
    ],
    queryFn: fetchMembers,
    enabled: page >= 0 && rowsPerPage > 0,
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const data = useMemo(() => queryData?.members || [], [queryData?.members]);
  const totalCount = queryData?.totalCount || 0;
  const totalPages = queryData?.totalPages || 0;

  const prepareChipData = (filters) => {
    let chipData = { ...filters };
    if (chipData.search_text) {
      delete chipData.search_text;
    }
    if (chipData.statuss == "all") {
      delete chipData.statuss;
    }

    if (chipData.filter === "create_base") {
      chipData.filter = "Created Date";
    }
    if (chipData.filter === "review_date") {
      chipData.filter = "Review Date";
    }
    return chipData;
  };

  const searchFunction = () => {
    setPage(0);
    setPageCount(1);
    const newFilter = { ...filterState, search_text: searchText };
    setAppliedFilterState(newFilter);
    setFilterStateUpdated(prepareChipData(newFilter));
    handleCloseFilterDrawer();
    localStorage.setItem("MemberListSearch", JSON.stringify(newFilter));
  };

  const handleDeleteChip = (chipData) => {
    setPage(0);
    setPageCount(1);
    setFilterState(chipData);
    setAppliedFilterState(chipData);
    setFilterStateUpdated(prepareChipData(chipData));
    localStorage.setItem("MemberListSearch", JSON.stringify(chipData));
  };

  const handleClearFilter = () => {
    let clear_data = { ...EMPTY_FILTER };
    setPage(0);
    setPageCount(1);
    setSearchText("");
    setFilterState(clear_data);
    setAppliedFilterState(clear_data);
    setFilterStateUpdated(prepareChipData(clear_data));
    handleCloseFilterDrawer();
    localStorage.removeItem("MemberListSearch");
  };

  const handleOpenReviewDateModal = (member) => {
    setSelectedMemberForReviewDate(member);
    setOpenReviewDateModal(true);
  };

  const handleOpenFilterDrawer = () => {
    setFilterDrawerState(true);
  };

  const handleCloseFilterDrawer = () => {
    setFilterDrawerState(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (newPage <= 0) {
      setPageCount(1);
    } else {
      setPageCount(newPage + 1);
    }
  };

  const handleChangePages = (event, newPage) => {
    if (newPage <= 0) {
      setPage(0);
      setPageCount(1);
    } else {
      setPage(newPage - 1);
      setPageCount(newPage);
    }
    localStorage.setItem("member_page_number", newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setPageCount(1);
  };

  const handleFilterByName = (event) => {
    setSearchText(event.target.value);
  };

  const isUserNotFound = data.length === 0;

  const LoginAsMember = async (value) => {
    const data = {
      email: value.email,
      user_type: "client",
    };

    const result = await member_login_api(data);
    if (result.code === 200) {
      window.open(`${clientPortalUrl}login-by-admin/${result?.token}`);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const WeightTracker = (value) => {
    navigate(`/member/weight-tracker/${value.id}`, {
      state: value,
    });
  };

  const handleEdit = (value) => {
    navigate(`/member/edit-member/${value.id}`, {
      state: value,
    });
  };

  const handleReviewDateSuccess = ({ changed }) => {
    queryClient.setQueriesData(
      { queryKey: ["members"], exact: false },
      (old) => {
        if (!old?.members) return old;
        return {
          ...old,
          members: old.members.map((member) => {
            if (member._id !== selectedMemberForReviewDate?._id) return member;
            return {
              ...member,
              programs: member.programs.map((p) => {
                const updated = changed.find((c) => c._id === p._id);
                if (updated) {
                  return {
                    ...p,
                    review_date: updated.review_date,
                    use_individual_review_date:
                      updated.use_individual_review_date,
                  };
                }
                return p;
              }),
            };
          }),
        };
      },
    );
  };

  const handleOpenNotePad = (member) => {
    const hasPrograms = member?.programs?.length > 0;
    const fullName = `${member.first_name} ${member.last_name}`;

    const baseData = {
      title: fullName,
      images: null,
      member_id: member._id,
    };

    if (hasPrograms) {
      const notepadData = {
        ...baseData,
        _id: member.programs[0]?._id,
        program_list: member.programs,
        message:
          "This notepad is specific to this member for the selected programme.",
        type_member: true,
      };
      setSelectedNotepad(notepadData);
    } else {
      const notepadData = {
        ...baseData,
        message:
          "This notepad is specific to this member only. (Member's programming notepad)",
      };
      setSelectedNotepad(notepadData);
    }

    setIsOpenNotePad(true);
  };

  const handleChat = (value) => {
    navigate(`/chat/?chat_id=${value.id}`, {
      state: value,
    });
  };

  const handleFormList = (value) => {
    setSelectedFormsMember(value.object || value);
    setIsOpenFormsList(true);
  };

  const handleProfile = (value) => {
    navigate(`/member/member-profile/${value.id}`, {
      state: value,
    });
  };

  const handlePoints = (value) => {
    navigate(`/member/member-points/${value.id}`, {
      state: value,
    });
  };

  const handleChangePassword = (value) => {
    navigate(`/member/change-password/${value.id}`, {
      state: value,
    });
  };

  const handleChangeRoute = (route, data) => {
    navigate(route, { state: data });
  };

  const headers = [
    { label: "First Name", key: "first_name" },
    { label: "Last Name", key: "last_name" },
    { label: "Email", key: "email" },
    { label: "Programme", key: "program" },
    { label: "Created By", key: "created_by" },
    { label: "Created At", key: "created_at" },
  ];

  function download_csv_file(csv, csvFileData) {
    const headerRow = csv.map((column) => column.label);
    const csvRows = [headerRow];

    csvFileData.forEach((row) => {
      const dataRow = csv.map((column) => {
        const value = row[column.key] || "";
        return `"${value.toString().replace(/"/g, '""')}"`;
      });

      csvRows.push(dataRow);
    });

    const csvContent = csvRows.map((row) => row.join(",")).join("\n");

    const hiddenElement = document.createElement("a");
    hiddenElement.href = "data:text/csv;charset=utf-8," + encodeURI(csvContent);
    hiddenElement.target = "_blank";
    hiddenElement.download = "data.csv";
    hiddenElement.click();
  }

  const handleNavigateImportMember = () => {
    download_csv_file(headers, csvData);
  };

  const handleNavigate = () => {
    navigate(`/member/add-member`);
  };
  const handleAgreeDelete = (value) => {
    setDeleteDoc(value);
    setOpenDelete(true);
  };

  const removeMembersFromCache = (ids) => {
    queryClient.setQueriesData(
      { queryKey: ["members"], exact: false },
      (old) => {
        if (!old || !old.members) {
          return old;
        }

        const filteredMembers = old.members.filter((member) => {
          const memberId = member?._id;
          return !ids.includes(memberId);
        });

        if (filteredMembers.length === old.members.length) {
          return old;
        }

        const removedCount = old.members.length - filteredMembers.length;
        return {
          ...old,
          members: filteredMembers,
          totalCount: Math.max((old.totalCount || 0) - removedCount, 0),
        };
      },
    );
  };

  const handleMemberSubscribers = (value) => {
    let route = `${listing_name}member-subscribers/${value._id}`;
    handleChangeRoute(route, value);
  };

  const handleDelete = async () => {
    setOpenDelete(false);
    const previousQueries = queryClient.getQueriesData({
      queryKey: ["members"],
      exact: false,
    });
    removeMembersFromCache([deleteDoc.id]);

    const result = await DeleteMemberApi(deleteDoc.id);
    if (result.code === 200) {
      await queryClient.invalidateQueries({
        queryKey: ["members"],
        exact: false,
      });
      enqueueSnackbar(result.message, { variant: "success" });
    } else {
      previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  const handleMultipleDelete = async () => {
    setOpenMultiDelete(false);

    const member_id = [];
    selected.map((id) => {
      member_id.push(id);
    });
    const formData = new FormData();
    formData.append("type", "specific");
    formData.append("member", JSON.stringify(member_id));

    const previousQueries = queryClient.getQueriesData({
      queryKey: ["members"],
      exact: false,
    });
    removeMembersFromCache(member_id);

    const result = await DeleteMultiMemberApi(formData);
    if (result.code === 200) {
      enqueueSnackbar(result.message, { variant: "success" });
      setSelected([]);
      await queryClient.invalidateQueries({
        queryKey: ["members"],
        exact: false,
      });
    } else {
      previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };
  const handleIsDateRange = (event) => {
    const value = event.target.checked;
    setFilterState((values) => ({ ...values, is_date_range: value }));
  };
  const handleChangeOthers = (event, name) => {
    setFilterState((values) => ({ ...values, [name]: event }));
  };
  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setFilterState((values) => ({ ...values, [name]: value }));
  };

  const MENU_OPTIONS = [
    {
      label: "Edit",
      icon: "akar-icons:edit",
      handleClick: handleEdit,
    },
    {
      label: "Notepad",
      icon: "tabler:notebook",
      handleClick: handleOpenNotePad,
    },
    {
      label: "Review Date",
      icon: "mdi:calendar-check",
      handleClick: handleOpenReviewDateModal,
    },
    {
      label: "Login As Member",
      icon: "basil:login-solid",
      handleClick: LoginAsMember,
    },
    {
      label: "Chat",
      icon: "fluent:chat-28-filled",
      handleClick: handleChat,
    },
    {
      label: "Weight Tracker",
      icon: "mdi:weight-lifter",
      handleClick: WeightTracker,
    },
    {
      label: "View Forms",
      icon: "fluent:form-48-filled",
      handleClick: handleFormList,
    },
    {
      label: "View Profile",
      icon: "iconoir:profile-circled",
      handleClick: handleProfile,
    },
    {
      label: "View Points History",
      icon: "game-icons:two-coins",
      handleClick: handlePoints,
    },
    {
      label: "View Subscriptions",
      icon: "eva:eye-fill",
      handleClick: handleMemberSubscribers,
    },
    {
      label: "Delete",
      icon: "ant-design:delete-twotone",
      handleClick: handleAgreeDelete,
    },

    {
      label: "Change Password",
      icon: "ant-design:lock",
      handleClick: handleChangePassword,
    },
  ];

  useEffect(() => {
    let filter_data = EMPTY_FILTER;
    let find_filter = localStorage.getItem("MemberListSearch");
    if (find_filter) {
      filter_data = JSON.parse(find_filter);
      if (filter_data.search_text) {
        setSearchText(filter_data.search_text);
      }
    }
    setFilterState(filter_data);
    setAppliedFilterState(filter_data);
    setFilterStateUpdated(prepareChipData(filter_data));
  }, []);

  useEffect(() => {
    const csvExportData = data?.map((member) => {
      return {
        first_name: member?.first_name,
        last_name: member?.last_name,
        email: member?.email,
        program:
          member.programs && member.programs.length > 0
            ? member.programs.map((program) => program?.title).join("; ")
            : "",
        created_by: member.created_by,
        created_at: member.created_at,
      };
    });

    setCsvData(csvExportData);
  }, [data]);

  if (isLoading || (isFetching && !data.length)) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <>
      <CustomConfirmation
        open={openDelete}
        setOpen={setOpenDelete}
        title={"Are you sure you want to delete ?"}
        handleAgree={handleDelete}
      />
      <CustomConfirmation
        open={openMultiDelete}
        setOpen={setOpenMultiDelete}
        title={"Are you sure you want to delete ?"}
        handleAgree={handleMultipleDelete}
      />
      {selectedMemberForReviewDate && (
        <ReviewDateMemberModal
          member={selectedMemberForReviewDate}
          isOpenPop={openReviewDateModal}
          isClosePop={() => {
            setOpenReviewDateModal(false);
            setSelectedMemberForReviewDate(null);
          }}
          onSuccess={handleReviewDateSuccess}
        />
      )}
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-lg-6 col-sm-12">
            <h2>Members</h2>
            {harmonyRingEnabled && (
              <p className="text-muted">
                The highlighted members below are non-compliance members due to
                incomplete tasks from their Harmony Ring programme.
              </p>
            )}
          </div>
          <div className="col-lg-6 col-sm-12 text-end ">
            <button
              className="small-contained-button me-2 mt-1 mb-4"
              onClick={handleOpenFilterDrawer}
            >
              Filters &nbsp;&nbsp; <Icon icon={roundFilterList} />
            </button>
            <button
              onClick={handleNavigateImportMember}
              className="small-contained-button me-2"
            >
              Export Csv &nbsp;&nbsp; <Icon icon={"ic:outline-file-download"} />
            </button>
            <button onClick={handleNavigate} className="small-contained-button">
              Add Member
            </button>
          </div>
        </div>
        <div className="">
          <FilteredChipNew
            data={filterStateUpdated}
            tempState={filterState}
            EMPTY_FILTER={EMPTY_FILTER}
            onDeleteChip={handleDeleteChip}
            onClear={handleClearFilter}
          />
        </div>

        <Card style={{ overflowX: "auto" }}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex flex-column align-items-start">
              <TablePagination
                rowsPerPageOptions={[10, 50, 100, 200, 500]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={() => null}
              />
              <Pagination
                count={totalPages + 1}
                page={pageCount}
                defaultPage={0}
                onChange={handleChangePages}
                className="pagination-style"
              />
            </div>
            <div className="d-flex align-items-center">
              <UserListToolbar
                numSelected={selected.length}
                filterName={searchText}
                onFilterName={handleFilterByName}
                onSearch={searchFunction}
              />
              <button
                onClick={() => searchFunction()}
                className="small-contained-button me-3 "
              >
                Search
              </button>
            </div>
          </div>

          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <UserListHead
                headLabel={TABLE_HEAD}
                rowCount={data.length}
                numSelected={selected.length}
              />
              <TableBody>
                {data.map((row, i) => {
                  const {
                    id,
                    name,
                    status,
                    first_name,
                    last_name,
                    email,
                    profile,
                    programs,
                    createdBy,
                    created_at,
                    action_info,
                    non_compliance_member,
                  } = row;
                  const isItemSelected = selected.indexOf(id) !== -1;
                  return (
                    <TableRow
                      hover
                      key={id}
                      tabIndex={-1}
                      role="checkbox"
                      selected={isItemSelected}
                      aria-checked={isItemSelected}
                      sx={{
                        backgroundColor: non_compliance_member
                          ? "#ffa48d8a"
                          : "white",
                        "&:hover": {
                          backgroundColor: non_compliance_member
                            ? "#ffa48d8a !important"
                            : "white",
                        },
                      }}
                    >
                      <TableCell>
                        <CustomPopoverSection menu={MENU_OPTIONS} data={row} />
                      </TableCell>
                      <TableCell align="left">
                        {page * rowsPerPage + (i + 1)}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <div
                          className="d-flex align-items-start cursor profileRow"
                          onClick={() => {
                            handleProfile(row);
                          }}
                        >
                          <Avatar alt={name} src={s3baseUrl + profile} />
                          <div className="ms-2 profileText">
                            <Typography
                              variant="subtitle2"
                              className="profileName mb-0"
                              noWrap
                            >
                              {first_name} {last_name ? last_name : ""}
                            </Typography>

                            <Typography
                              variant="caption"
                              className="profileEmail"
                              noWrap
                              sx={{ color: "text.secondary" }}
                            >
                              {email ? email : "N/A"}
                            </Typography>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        className="program-exp"
                      >
                        {programs && programs.length > 0
                          ? programs?.map((program) => {
                              return (
                                <>
                                  <p className="mb-1">{program?.title}</p>
                                </>
                              );
                            })
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="column"
                          alignItems="start"
                          spacing={0}
                        >
                          <Typography
                            className="text-capital"
                            variant="subtitle2"
                            noWrap
                          >
                            {createdBy}
                          </Typography>
                          {createdBy == "coach" && (
                            <Typography
                              sx={{ fontSize: 11, opacity: 0.5 }}
                              variant="subtitle2"
                              noWrap
                            >
                              {action_info?.first_name +
                                " " +
                                action_info?.last_name +
                                " (" +
                                action_info?.email +
                                ")"}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{created_at}</TableCell>

                      <TableCell align="left">
                        <Label
                          variant="ghost"
                          color={status === false ? "error" : "success"}
                        >
                          {status === false ? "InActive" : "Active"}
                        </Label>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              {isUserNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={14} sx={{ py: 3 }}>
                      <SearchNotFound />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 50, 100, 200, 500]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </div>
      <CustomDrawer
        isOpenDrawer={filterDrawerState}
        onOpenDrawer={handleOpenFilterDrawer}
        onCloseDrawer={handleCloseFilterDrawer}
        pageTitle="Filters"
        componentToPassDown={
          <MemberFilter
            filterData={filterState}
            handleChange={handleChange}
            setFilterData={setFilterState}
            handleIsDateRange={handleIsDateRange}
            handleChangeOthers={handleChangeOthers}
            searchSubmitFilter={searchFunction}
            handleClearFilter={handleClearFilter}
          />
        }
      />
    </>
  );
}
